import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useCart } from '../contexts/CartContext';
import SurveySection from '../components/SurveySection';

const PaiementReussi = () => {
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('loading'); // 'loading' | 'paid' | 'pending' | 'error'
  const [surveyEmail, setSurveyEmail] = useState('');
  const [surveyDone, setSurveyDone] = useState(false);
  const hasRun = useRef(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  useEffect(() => {
    // Éviter un double appel (StrictMode en dev)
    if (hasRun.current) return;
    hasRun.current = true;

    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const confirm = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/payment/confirm`, {
          params: { session_id: sessionId },
        });

        if (data?.success && data.paid) {
          clearCart();
          // Email pour le sondage : priorité au backend, sinon celui mémorisé avant Stripe
          const storedEmail = localStorage.getItem('vs_survey_email') || '';
          setSurveyEmail(data.email || storedEmail);
          localStorage.removeItem('vs_survey_email');
          setStatus('paid');
        } else if (data?.success && !data.paid) {
          setStatus('pending');
        } else {
          setStatus('error');
        }
      } catch (err) {
        console.error('Erreur confirmation paiement:', err);
        setStatus('error');
      }
    };

    confirm();
  }, [searchParams, API_URL, clearCart]);

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Box sx={{ textAlign: 'center', p: 4 }}>
        {status === 'loading' && (
          <>
            <CircularProgress sx={{ mb: 3 }} />
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Vérification de votre paiement…
            </Typography>
            <Typography variant="body1" sx={{ color: '#666' }}>
              Merci de patienter quelques secondes.
            </Typography>
          </>
        )}

        {status === 'paid' && (
          <>
            <CheckCircleIcon sx={{ fontSize: 80, color: '#2e7d32', mb: 3 }} />
            <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
              Paiement confirmé !
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>
              Merci ! Votre paiement par carte a bien été reçu et vos inscriptions sont confirmées.
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>
              📧 Un courriel de confirmation avec votre facture payée vous a été envoyé.{' '}
              <strong>Pensez à vérifier vos courriels indésirables.</strong>
            </Typography>

            {surveyEmail && !surveyDone && (
              <Box sx={{ maxWidth: 560, mx: 'auto', textAlign: 'left', mt: 2 }}>
                <SurveySection email={surveyEmail} onSurveySubmitted={() => setSurveyDone(true)} />
              </Box>
            )}
          </>
        )}

        {status === 'pending' && (
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            Votre paiement n'a pas encore été confirmé par notre système. Si vous avez bien payé,
            il sera traité sous peu. Pour toute question, contactez-nous à info@vitasecours.ca.
          </Alert>
        )}

        {status === 'error' && (
          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
            Une erreur est survenue lors de la vérification de votre paiement. Si un montant a été
            débité, contactez-nous à info@vitasecours.ca avec votre nom et nous régulariserons votre
            inscription.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 5 }}>
          <Button component={Link} to="/formations" variant="contained" sx={{ minWidth: 200 }}>
            Voir d'autres formations
          </Button>
          <Button component={Link} to="/" variant="outlined" sx={{ minWidth: 200 }}>
            Retour à l'accueil
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default PaiementReussi;
